<template>
  <div class="main-page">
    <organization-breadcrumb
      :items="[$t('study_sets.curriculum'), $t('assignments.class_assignment'), currentAssignmentLibrary.name]"
    />
    <div id="page-content">
      <el-row type="flex" justify="center">
        <el-col :xs="24" :md="22">
          <div class="sub-header flex items-center">
            <div class="flex justify-between" style="width: 100%">
              <div>
                <div class="assignment-name-group">
                  <span @click="$router.go(-1)"><i class="el-icon-back"></i></span>
                  <rename-form
                    v-if="editMode"
                    :oldName="currentAssignmentLibrary.name"
                    v-on:cancel-rename="cancelRename"
                    v-on:handle-edit-name="handleEditName"
                    :is_rename_assignment_lib="true"
                  >
                  </rename-form>
                  <p v-else class="assignment-name">
                    {{ currentAssignmentLibrary.name }}
                    <el-tooltip
                      class="item"
                      :content="$t('assignments.details.edit_name')"
                      placement="top"
                      :visible-arrow="false"
                    >
                      <span class="edit-name" @click="() => (editMode = true)">
                        <i class="elsa-icon-edit2" />
                      </span>
                    </el-tooltip>
                  </p>
                </div>
                <p style="margin: 0 0 40px 0">
                  <span class="assignment-info">
                    <font-awesome-icon :icon="['fas', 'clock']" />
                    {{ $t("homework_library.created_on") }}
                    <b>{{ formatDate(currentAssignmentLibrary.created_at) }}</b>
                  </span>
                  <span class="assignment-info">
                    <font-awesome-icon :icon="['fas', 'users']" />
                    {{ $t("homework_library.table.assigned_to") }}:
                    <b>{{ formatClassCount(currentAssignmentLibrary.assigned.length) }}</b>
                  </span>
                </p>
              </div>
              <div class="flex" style="margin-top: 40px">
                <el-tooltip
                  class="item"
                  :content="$t('homework_library.table.duplicate')"
                  placement="top"
                  :visible-arrow="false"
                >
                  <tertiary-button class="assignment-action" @click.native="handleDuplicateAssignment">
                    <font-awesome-icon icon="copy" />
                  </tertiary-button>
                </el-tooltip>
                <el-tooltip class="item" content="Remove" placement="top" :visible-arrow="false">
                  <tertiary-button class="assignment-action remove-btn" @click.native="handleDeleteAssignment">
                    <font-awesome-icon :icon="['fas', 'trash-alt']" style="color: #eb5757;" />
                  </tertiary-button>
                </el-tooltip>
                <span id="assign-to-group-btn" class="assign-to-class" @click="handleAssignToClass">
                  <chip>
                    {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_group`)) }}
                  </chip>
                </span>
              </div>
            </div>
          </div>
          <section>
            <lesson-sets
              v-if="
                currentAssignmentLibrary.lesson_sets &&
                  currentAssignmentLibrary.lesson_sets.length > 0 &&
                  currentAssignmentLibrary.lesson_sets[0]['id']
              "
              :data="currentAssignmentLibrary.lesson_sets"
              :loading="loading"
            />
            <study-sets
              v-if="currentAssignmentLibrary.study_sets && currentAssignmentLibrary.study_sets.length > 0"
              :data="currentAssignmentLibrary.study_sets"
              :loading="loading"
            />
            <assessments
              v-if="currentAssignmentLibrary.assessments && currentAssignmentLibrary.assessments.length > 0"
              :assessments="currentAssignmentLibrary.assessments"
            />
          </section>
          <section>
            <header class="section-header">
              {{ $t($i18nLabelsHelper(currentOrganizationType, `homework_library.assigned_group`)) }}
            </header>
            <assigned-classes
              :assigned-classes="currentAssignmentLibrary.assigned"
              :lesson-types="currentAssignmentLibrary.lesson_types"
              :loading="loading"
            />
          </section>
        </el-col>
      </el-row>
    </div>
    <confirm-delete
      page="detail"
      :dialogVisible="modals.showConfirmDelete"
      append-to-body
      v-on:closed="modals.showConfirmDelete = false"
      v-on:delete="handleConfirmDeleteAssignment"
    />
    <assign-to-class-wrapper ref="assignToClass" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import Chip from "@/components/common/Chip";
import { format } from "date-fns";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import LessonSets from "@/components/homeworks/details/LessonSets";
import StudySets from "@/components/homeworks/details/StudySets";
import AssignedClasses from "@/components/homeworks/details/AssignedClasses";
import ConfirmDelete from "@/components/modals/homeworks/ConfirmDelete";
import { DATE_FORMAT } from "@/constants/generic";
import AssignToClassWrapper from "@/components/modals/homeworks/AssignToClassWrapper";
import RenameForm from "@/components/common/RenameForm";
import Assessments from "@/components/homeworks/details/Assessments";

export default {
  props: {
    assignmentLibraryId: null,
  },
  components: {
    TertiaryButton,
    Chip,
    OrganizationBreadcrumb,
    LessonSets,
    StudySets,
    AssignedClasses,
    ConfirmDelete,
    AssignToClassWrapper,
    RenameForm,
    Assessments,
  },
  computed: {
    ...mapGetters({
      getCurrentClass: "classes/getCurrentClass",
      currentOrganizationType: "organization/currentOrganizationType",
      currentOrganizationId: "organization/currentOrganizationId",
    }),
  },
  data() {
    return {
      modals: {
        showConfirmDelete: false,
      },
      currentAssignmentLibrary: {
        name: null,
        created_at: null,
        assigned: [],
        lesson_sets: [],
        study_sets: [],
      },
      loading: true,
      editMode: false,
    };
  },
  async mounted() {
    try {
      await this.getAssignmentLibrary();
    } catch (error) {
      return false;
    }
  },
  methods: {
    ...mapActions({
      setCurrentAssignment: "homeworks/setCurrentAssignment",
      deleteAssignmentLibrary: "homeworks/deleteAssignmentLibrary",
      updateAssignmentLib: "homeworks/updateAssignmentLib",
    }),
    formatDate(date) {
      return date ? format(new Date(date), DATE_FORMAT) : "--";
    },
    formatClassCount(number) {
      if (number > 1) {
        return `${number} ${this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `groups`))}`;
      } else {
        return `${number} ${this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `group`))}`;
      }
    },
    backToList() {
      this.$router.push({
        name: "assignment-library",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    handleDuplicateAssignment() {
      this.$router.push({
        name: "assignment-library-duplicate",
        params: {
          organizationId: this.currentOrganizationId,
          assignmentLibraryId: this.assignmentLibraryId,
        },
      });
    },
    async handleConfirmDeleteAssignment() {
      try {
        await this.deleteAssignmentLibrary(this.assignmentLibraryId);
        successMessage(this.$t("assignments.delete_assignment_success"))(this);
        return this.backToList();
      } catch (error) {
        return warningMessage(this.$t("assignments.delete_assignment_warning"))(this);
      }
    },
    handleDeleteAssignment() {
      this.modals.showConfirmDelete = true;
    },
    handleAssignToClass() {
      this.setCurrentAssignment(this.currentAssignmentLibrary);
      this.$refs.assignToClass.handleAssignToClass(this.currentAssignmentLibrary);
    },
    async getAssignmentLibrary() {
      this.loading = true;
      const params = { type: "full" };
      const { data } = await this.$axios.get(`/v1/homeworks/${this.assignmentLibraryId}/assignment-library-detail`, {
        params,
      });
      this.currentAssignmentLibrary = data;
      this.loading = false;
    },
    async handleEditName(rename_ass_assigned = false, newName) {
      const payload = { name: newName, rename_ass_assigned };
      this.loading = true;
      this.editMode = false;
      try {
        await this.updateAssignmentLib({
          assignmentLibraryId: this.assignmentLibraryId,
          payload: payload,
        });
        await this.getAssignmentLibrary();
        successMessage(this.$t("common.update_name_success"))(this);
      } catch (error) {
        warningMessage(this.$t("common.generic_error_message"))(this);
        return false;
      } finally {
        this.loading = false;
      }
    },
    cancelRename() {
      this.editMode = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.main-page {
  overflow: hidden;
  width: 100%;
}
.assignment-name-group {
  margin: 40px 0 8px 0px;
  display: flex;
  > span {
    font-size: 25px;
    padding-right: 5px;
  }
}
.assignment-name {
  font-size: 32px;
  line-height: 48px;
  font-weight: 700;
  display: flex;
  margin-top: -10px;
  margin-bottom: 0px;
}

.assignment-info {
  color: #8e8e93;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  margin-left: 32px;

  + .assignment-info {
    margin-left: 32px;
  }
}

::v-deep .assignment-action span {
  font-weight: 300;
  font-size: 16px;
  color: #6d7a8c;
  cursor: pointer;
}

.el-button + .el-button {
  margin-left: 16px;
}

.elsa-flat-button.el-button {
  height: 48px !important;
  width: 48px;
  border: 1px solid #e5e5ea;
  min-width: 48px;
}

.remove-btn:hover {
  background: rgba(235, 87, 87, 0.2) !important;
  border: 1px solid #e75656 !important;
}

.remove-btn {
  margin-right: 16px;
}

.elsa-flat-button.el-button:hover {
  background: #e6f2ff;
  border: 1px solid #007eff;
}

.section-header {
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  margin-bottom: 32px;
  text-transform: uppercase;
  letter-spacing: 00.25px;
  padding: 0 32px;
}

::v-deep .el-form {
  padding-bottom: 0px;
}

::v-deep .assign-to-class .elsa-chip {
  text-transform: unset !important;
  height: 48px;
}
.edit-name {
  color: #478dff;
  cursor: pointer;
  padding: 0px 13px;
  margin-top: 6px;
}
</style>
